/**
 * Projects — Liste et gestion des projets
 */

import { useState, useEffect } from "react";
import { api } from "../lib/api";
import type { GitHubProjectsSyncPreview } from "../lib/api";
import type { Project } from "@synapse/shared";

type SyncEventResult = {
  label?: string;
  message?: string;
};

type SyncEventDetail = {
  handled?: boolean;
  promise?: Promise<SyncEventResult | void>;
};

const ENV_COLORS: Record<string, string> = {
  dev: "var(--cyan)",
  stage: "var(--blue)",
  prod: "var(--clay)",
};

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [preview, setPreview] = useState<GitHubProjectsSyncPreview | null>(null);

  const load = async (throwOnError = false) => {
    try {
      setLoading(true);
      setProjects(await api.projects.list());
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur";
      setError(message);
      if (throwOnError) {
        throw err instanceof Error ? err : new Error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Le bouton « Synchroniser » ne modifie plus rien directement : il calcule
   * d'abord le plan (dry-run) et ouvre une confirmation. La synchronisation
   * réelle n'est déclenchée qu'après validation explicite.
   */
  const previewGitHubSync = async (): Promise<SyncEventResult> => {
    const result = await api.github.previewProjectsSync();

    if (result.linked === 0 && result.created === 0) {
      return {
        label: "Sync GitHub",
        message: `Déjà à jour · ${result.fetchedRepos} dépôt(s) vérifié(s)`,
      };
    }

    setPreview(result);

    return {
      label: "Sync GitHub",
      message: `${result.created} à créer, ${result.linked} à lier — confirmation requise`,
    };
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const handleSync = (event: Event) => {
      const syncEvent = event as CustomEvent<SyncEventDetail>;
      if (syncEvent.detail) {
        syncEvent.detail.handled = true;
        syncEvent.detail.promise = previewGitHubSync();
      }
    };

    window.addEventListener("synapse:sync", handleSync);
    return () => window.removeEventListener("synapse:sync", handleSync);
  }, []);

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">portefeuille</div>
          <h1>Projets</h1>
          <p>{projects.length} projet{projects.length > 1 ? "s" : ""}</p>
        </div>
        <div className="header-actions">
          <button className="btn primary" onClick={() => setShowNew(true)}>+ Nouveau projet</button>
        </div>
      </div>

      {error && <div className="ops-card" style={{ borderColor: "var(--rust)" }}><p style={{ color: "var(--rust)" }}>⚠ {error}</p></div>}

      {loading && <div className="no-results">Chargement…</div>}

      {!loading && !error && (
        <div className="projects-grid">
          {projects.map((project) => (
            <div key={project.id} className="project-card">
              <div className="p-head">
                <div className="p-name" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span className="pdot" style={{ width: "12px", height: "12px", background: project.color || "var(--purple)", borderRadius: "999px" }} />
                  {project.name}
                </div>
              </div>
              {project.description && (
                <p style={{ color: "var(--muted)", fontSize: "13px" }}>{project.description}</p>
              )}
              <div className="p-envs">
                {Array.isArray(project.envs) && project.envs.map((env) => (
                  <span key={env} className="env-chip">
                    <span className="dot-sm" style={{ background: ENV_COLORS[env] || "var(--muted)" }} />
                    {env}
                  </span>
                ))}
              </div>
              {project.repo && (
                <div className="p-mode">
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>📦 {project.repo}</span>
                </div>
              )}
            </div>
          ))}
          {projects.length === 0 && !loading && (
            <div className="no-results">Aucun projet. Crée le premier !</div>
          )}
        </div>
      )}

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}

      {preview && (
        <SyncPreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onApplied={() => { setPreview(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Confirmation de la synchronisation GitHub — liste nommément les projets qui
 * seraient créés et liés avant toute écriture en base.
 */
function SyncPreviewModal({
  preview,
  onClose,
  onApplied,
}: {
  preview: GitHubProjectsSyncPreview;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [applying, setApplying] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const apply = async () => {
    setApplying(true);
    setFailure(null);
    try {
      await api.github.syncProjects();
      onApplied();
    } catch (err) {
      setFailure(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="modal-overlay show" onClick={applying ? undefined : onClose}>
      <div className="modal" style={{ maxWidth: "620px" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔄 Synchroniser avec GitHub</h2>
          <button className="modal-close" onClick={onClose} disabled={applying}>×</button>
        </div>

        <div style={{ padding: "24px", display: "grid", gap: "16px" }}>
          <p style={{ color: "var(--muted)", fontSize: "13px" }}>
            Organisation <strong>{preview.organization}</strong> — {preview.fetchedRepos} dépôt(s) analysé(s),
            {" "}{preview.ignored} ignoré(s) (archivé/désactivé).
          </p>

          {preview.willCreate.length > 0 && (
            <div>
              <div className="eyebrow">à créer ({preview.willCreate.length})</div>
              <ul style={{ margin: "8px 0 0", paddingLeft: "18px", display: "grid", gap: "4px" }}>
                {preview.willCreate.map((item) => (
                  <li key={item.repo} style={{ fontSize: "13px" }}>
                    {item.name}
                    <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
                      {" "}· {item.repo}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {preview.willLink.length > 0 && (
            <div>
              <div className="eyebrow">à lier ({preview.willLink.length})</div>
              <ul style={{ margin: "8px 0 0", paddingLeft: "18px", display: "grid", gap: "4px" }}>
                {preview.willLink.map((item) => (
                  <li key={item.repo} style={{ fontFamily: "var(--mono)", fontSize: "12px" }}>
                    {item.repo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failure && (
            <p style={{ color: "var(--rust)", fontSize: "13px" }}>⚠ {failure}</p>
          )}

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose} disabled={applying}>Annuler</button>
            <button type="button" className="btn primary" onClick={apply} disabled={applying}>
              {applying ? "Synchronisation…" : "Appliquer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#b794f6");
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.projects.create({ name, description, color, envs: ["dev"] });
      onCreated();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erreur");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay show" onClick={onClose}>
      <div className="modal" style={{ maxWidth: "500px" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🚀 Nouveau projet</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: "24px", display: "grid", gap: "18px" }}>
          <div className="form-group">
            <label>Nom</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Couleur</label>
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: "60px", height: "40px", borderRadius: "12px", border: "none", background: "transparent" }} />
          </div>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={creating || !name.trim()}>{creating ? "…" : "Créer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
